import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { DollarSign, TrendingUp, CheckCircle, Clock, Globe } from "lucide-react";
import { formatSydney, formatTableDateSydney, getCurrentSydneyDisplay } from "../../utils/timezone";

export default function StaffCommissions() {
  const { api } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverTime, setServerTime] = useState(null);

  useEffect(() => {
    const fetchCommissions = async () => {
      try {
        const [commissionsRes, timeRes] = await Promise.all([
          api().get("/commissions"),
          api().get("/server-time"),
        ]);
        setCommissions(commissionsRes.data);
        setServerTime(timeRes.data);
      } catch (error) {
        toast.error("Failed to load commissions");
      } finally {
        setLoading(false);
      }
    };
    fetchCommissions();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const totalCommission = commissions.reduce((sum, c) => sum + (c.commission_amount || 0), 0);
  const paidCommission = commissions.filter(c => c.status === "paid").reduce((sum, c) => sum + (c.commission_amount || 0), 0);
  const pendingCommission = commissions.filter(c => c.status === "pending").reduce((sum, c) => sum + (c.commission_amount || 0), 0);

  return (
    <div className="space-y-8" data-testid="staff-commissions">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">My Commissions</h1>
        <p className="text-slate-500 mt-1">View your commission statements</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-700 font-mono">${totalCommission.toLocaleString()}</p>
                <p className="text-sm text-emerald-600">Total Earned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">${paidCommission.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Paid</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700 font-mono">${pendingCommission.toLocaleString()}</p>
                <p className="text-sm text-amber-600">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commissions List */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Commission Statements</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Signups</th>
                  <th>Total Amount</th>
                  <th>Rate</th>
                  <th>Commission</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {commissions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-500">
                      No commission statements yet
                    </td>
                  </tr>
                ) : (
                  commissions.map((c) => (
                    <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                      <td className="font-medium">
                        {formatSydney(c.period_start, "MMM dd")} - {formatTableDateSydney(c.period_end)}
                      </td>
                      <td className="font-mono">{c.total_signups}</td>
                      <td className="font-mono">${c.total_amount?.toLocaleString()}</td>
                      <td className="font-mono">{c.commission_rate}%</td>
                      <td className="font-mono font-medium text-emerald-600">
                        ${c.commission_amount?.toLocaleString()}
                      </td>
                      <td>
                        <Badge className={c.status === "paid" ? "badge-approved" : "badge-pending"}>
                          {c.status === "paid" ? "Paid" : "Pending"}
                        </Badge>
                      </td>
                      <td className="text-slate-500 text-sm max-w-[150px] truncate">
                        {c.notes || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
